import { getUserData } from './api/auth';
import { getUserRole, isPlatformAdmin } from './roleUtils';

/** Canonical admin landing routes (sidebar order) used for post-login and permission fallbacks */
export const ADMIN_ROUTE_REGISTRY = [
  { path: '/admin/dashboard', permission: 'dashboard.view' },
  { path: '/admin/companies', permission: 'companies.view' },
  { path: '/admin/plans', permission: 'plans.view' },
  { path: '/admin/industries', permission: 'industries.view' },
  { path: '/admin/master-skills', permission: 'master_skills.view' },
  { path: '/admin/staff', permission: 'staff.view' },
  { path: '/admin/roles', permission: 'roles.view' },
  { path: '/admin/job-sources', permission: 'job_sources.view' },
  { path: '/admin/scheduled-fetch', permission: 'scheduled_fetch.view' },
  { path: '/admin/fetched-jobs', permission: 'fetched_jobs.view' },
  { path: '/admin/expired-jobs', permission: 'fetched_jobs.view' },
  { path: '/admin/system-settings', permission: 'system_settings.view' },
  { path: '/admin/accounts', permission: 'accounts.view' },
  { path: '/admin/system-logs', permission: 'system_logs.view' },
  { path: '/admin/support', permission: 'support.view' },
  { path: '/admin/careers', permission: 'careers_jobs.view' },
  { path: '/admin/blogs', permission: 'blogs.view' },
  { path: '/admin/tutorials', permission: 'tutorials.view' },
  { path: '/admin/features', permission: 'features.view' },
  { path: '/admin/faqs', permission: 'faqs.view' },
  { path: '/admin/testimonials', permission: 'testimonials.view' },
  { path: '/admin/terms', permission: 'terms.view' },
  { path: '/admin/privacy', permission: 'privacy.view' },
  { path: '/admin/refund', permission: 'refund.view' },
  { path: '/admin/newsletter', permission: 'newsletter.view', roles: ['super_admin', 'manager'] },
];

export const ADMIN_NO_ACCESS_PATH = '/admin/no-access';

/**
 * First admin route the current user may open (used after login and on permission denial).
 * @returns {string}
 */
export function getFirstAccessibleAdminRoute() {
  const userRole = getUserRole();
  if (userRole === 'super_admin' || userRole === 'manager') {
    if (canAccess('dashboard.view')) {
      return '/admin/dashboard';
    }
  }

  for (const route of ADMIN_ROUTE_REGISTRY) {
    if (route.roles && !route.roles.includes(userRole)) continue;
    if (canAccess(route.permission)) return route.path;
  }

  return ADMIN_NO_ACCESS_PATH;
}

/**
 * Resolve a safe redirect when the user lacks permission for the current route.
 * Avoids sending users back to the same path (prevents redirect loops).
 * @param {string} currentPath
 * @returns {string}
 */
export function resolveAdminPermissionFallback(currentPath) {
  const fallback = getFirstAccessibleAdminRoute();
  const normalizedCurrent = (currentPath || '').replace(/\/$/, '') || '/';
  const normalizedFallback = fallback.replace(/\/$/, '') || '/';

  if (
    normalizedCurrent === normalizedFallback ||
    normalizedCurrent === ADMIN_NO_ACCESS_PATH
  ) {
    return ADMIN_NO_ACCESS_PATH;
  }

  return fallback;
}

/**
 * Check if user has a specific permission
 * @param {string} permission - Permission name (e.g., 'companies.view')
 * @returns {boolean}
 */
export function hasPermission(permission) {
  const userData = getUserData();
  if (!userData) return false;

  // Super admin always has full access
  const userRole = getUserRole();
  if (userRole === 'super_admin') {
    return true;
  }

  const userPermissions = userData.permissions || [];

  // Super admin and admin have all permissions (wildcard check)
  if (userPermissions.includes('*')) {
    return true;
  }

  // Exact match
  if (userPermissions.includes(permission)) {
    return true;
  }

  // Wildcard check (e.g., 'companies.*' matches 'companies.view')
  const [category] = permission.split('.');
  const categoryWildcard = `${category}.*`;
  if (userPermissions.includes(categoryWildcard)) {
    return true;
  }

  return false;
}

/**
 * Check if user has any of the specified permissions
 * @param {Array<string>} permissions - Array of permission names
 * @returns {boolean}
 */
export function hasAnyPermission(permissions) {
  return permissions.some(permission => hasPermission(permission));
}

/**
 * Check if user has all of the specified permissions
 * @param {Array<string>} permissions - Array of permission names
 * @returns {boolean}
 */
export function hasAllPermissions(permissions) {
  return permissions.every(permission => hasPermission(permission));
}

/**
 * Get user permissions from token
 * @returns {Array<string>}
 */
export function getUserPermissions() {
  const userData = getUserData();
  return userData?.permissions || [];
}

/**
 * Check if user can access a route based on permissions
 * @param {string|Array<string>} requiredPermissions - Required permission(s)
 * @returns {boolean}
 */
export function canAccess(requiredPermissions) {
  if (!requiredPermissions) return true;

  const permissions = Array.isArray(requiredPermissions)
    ? requiredPermissions
    : [requiredPermissions];

  return hasAnyPermission(permissions);
}

/**
 * Sidebar / nav: super_admin, explicit permission, or legacy platform staff without RBAC role_id.
 * @param {string} [permission]
 * @returns {boolean}
 */
export function canAccessNavPermission(permission) {
  if (!permission) return true;

  const userRole = getUserRole();
  if (userRole === 'super_admin') return true;
  if (hasPermission(permission)) return true;

  const assigned = getUserPermissions();
  if (assigned.length === 0 && isPlatformAdmin()) {
    if (permission.startsWith('newsletter.')) {
      return userRole === 'manager';
    }
    return true;
  }

  return false;
}
